import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { formatMoney } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, Edit, Download, ArrowUpDown, FileText, FileSpreadsheet, FileType2, ChevronDown, ArrowDownAZ, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { WhatsAppChatIconButton, WhatsAppShareButton } from "@/components/WhatsAppShare";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from "@/components/ui/dropdown-menu";
import CustomerImportPreviewDialog from "@/components/CustomerImportPreviewDialog";
import { diagnoseCustomerRouting } from "@/lib/maps";

const SORT_OPTIONS = [
  { value: "nombre-asc", label: "Nombre A → Z" },
  { value: "nombre-desc", label: "Nombre Z → A" },
  { value: "fantasia-asc", label: "Nombre Fantasía A → Z" },
  { value: "provincia-asc", label: "Provincia A → Z" },
  { value: "canton-asc", label: "Cantón A → Z" },
  { value: "categoria-asc", label: "Categoría A → Z" },
  { value: "recientes", label: "Más recientes primero" },
  { value: "antiguos", label: "Más antiguos primero" },
];

/**
 * Inline pill on each customer row that flags routing-readiness:
 *   • broken_link  — red: link present but Maps doesn't expose lat/lng.
 *   • missing_link — amber: no link, no coords, no province either.
 *   • approximate  — silent (no badge) — fallback is acceptable.
 *   • ok           — silent.
 *
 * Hover/tap title spells out what to fix.
 */
function RoutingBadge({ customer }) {
  const { status, reason } = diagnoseCustomerRouting(customer);
  if (status === "ok" || status === "approximate") return null;
  const isBroken = status === "broken_link";
  return (
    <span
      data-testid={`routing-flag-${customer.id}`}
      data-routing-status={status}
      title={reason}
      className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${
        isBroken
          ? "bg-rose-50 text-rose-800 border-rose-200"
          : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
    >
      {isBroken ? "Link inválido" : "Sin ubicación"}
    </span>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sortBy, setSortBy] = useState("nombre-asc");
  const [sellers, setSellers] = useState([]);
  // Balances map (by_id + by_name) loaded once to render the "Saldo CxC" column.
  const [balances, setBalances] = useState({ by_id: {}, by_name: {} });

  useEffect(() => {
    api.get("/sellers").then((r) => setSellers(r.data || [])).catch(() => setSellers([]));
    api.get("/customers/balances").then((r) => setBalances(r.data || { by_id: {}, by_name: {} })).catch(() => {});
  }, []);

  const getBalanceFor = (c) => {
    const k = balances.by_id?.[c.id] || balances.by_name?.[c.nombre_facturacion];
    return k || { saldo_pendiente: 0, saldo_a_favor: 0, facturas_pendientes: 0 };
  };

  const sortedCustomers = useMemo(() => {
    const list = [...customers];
    const cmp = (a, b) => (a || "").toString().localeCompare((b || "").toString(), "es", { sensitivity: "base" });
    switch (sortBy) {
      case "nombre-desc":
        return list.sort((a, b) => cmp(b.nombre_facturacion, a.nombre_facturacion));
      case "fantasia-asc":
        return list.sort((a, b) => cmp(a.nombre_fantasia, b.nombre_fantasia));
      case "provincia-asc":
        return list.sort((a, b) => cmp(a.provincia, b.provincia) || cmp(a.canton, b.canton));
      case "canton-asc":
        return list.sort((a, b) => cmp(a.canton, b.canton));
      case "categoria-asc":
        return list.sort((a, b) => cmp(a.category, b.category));
      case "recientes":
        return list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      case "antiguos":
        return list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      case "nombre-asc":
      default:
        return list.sort((a, b) => cmp(a.nombre_facturacion, b.nombre_facturacion));
    }
  }, [customers, sortBy]);
  const [formData, setFormData] = useState({
    codigo: "",
    nombre_facturacion: "",
    nombre_fantasia: "",
    phone: "",
    phone2: "",
    email: "",
    provincia: "",
    canton: "",
    distrito: "",
    address: "",
    gam: "",
    category: "",
    actividad_economica: "",
    maps_link: "",
    notes: "",
    vendedor_asignado: ""
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, { ...formData, id: editingCustomer.id });
        toast.success("Cliente actualizado");
      } else {
        await api.post("/customers", formData);
        toast.success("Cliente creado");
      }
      setIsDialogOpen(false);
      setEditingCustomer(null);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error("Error al guardar cliente");
    }
  };

  const handleDelete = (id) => {
    setDeleteTarget({ type: 'single', id });
    setDeleteConfirmOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("No hay clientes seleccionados");
      return;
    }
    setDeleteTarget({ type: 'bulk' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget?.type === 'single') {
        await api.delete(`/customers/${deleteTarget.id}`);
        toast.success("Cliente eliminado");
      } else if (deleteTarget?.type === 'bulk') {
        const response = await api.post('/customers/bulk-delete', selectedIds);
        toast.success(response.data.message);
        setSelectedIds([]);
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchCustomers();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === customers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(customers.map(c => c.id));
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData(customer);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      codigo: "",
      nombre_facturacion: "",
      nombre_fantasia: "",
      phone: "",
      phone2: "",
      email: "",
      provincia: "",
      canton: "",
      distrito: "",
      address: "",
      gam: "",
      category: "",
      actividad_economica: "",
      maps_link: "",
      notes: "",
      vendedor_asignado: ""
    });
  };

  const downloadTemplate = async (format = "csv") => {
    try {
      const token = localStorage.getItem("token");
      const url = format === "excel"
        ? `${process.env.REACT_APP_BACKEND_URL}/api/customers/template/excel`
        : `${process.env.REACT_APP_BACKEND_URL}/api/customers/template`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objUrl;
      link.setAttribute('download', format === "excel" ? 'plantilla_clientes.xlsx' : 'plantilla_clientes.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Plantilla ${format === "excel" ? "Excel" : "CSV"} descargada`);
    } catch (error) {
      toast.error("Error al descargar plantilla");
    }
  };

  const exportCustomers = async (format, order) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/customers/export/${format}?order=${order}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw new Error("export failed");
      const blob = await response.blob();
      const ext = format === "excel" ? "xlsx" : format;
      const suffix = order === "zone" ? "zona" : "alfa";
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clientes_${suffix}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        `Clientes exportados (${ext.toUpperCase()}, ${order === "zone" ? "por zona" : "alfabético"})`,
      );
    } catch {
      toast.error("Error al exportar clientes");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      let text = await file.text();
      if (text.charCodeAt(0) === 0xFEFF) {
        text = text.slice(1);
      }
      
      const parseCSVLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        result.push(current.trim());
        return result;
      };
      
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error("El CSV está vacío o no tiene datos");
        return;
      }
      
      const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
      
      const previewData = [];
      for (let i = 1; i < Math.min(lines.length, 11); i++) {
        const values = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        previewData.push(row);
      }
      
      window._customerCsvFile = file;
      setCsvPreviewData(previewData);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      toast.error("Error al leer el archivo CSV");
    }
    
    e.target.value = '';
  };

  const finalizarImportacion = async () => {
    if (!window._customerCsvFile) {
      toast.error("No hay archivo para importar");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", window._customerCsvFile);

      const response = await api.post("/customers/import", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      setIsPreviewDialogOpen(false);
      setCsvPreviewData([]);
      window._customerCsvFile = null;
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar clientes");
    }
  };

  if (loading) {
    return <PageLoader label="Cargando clientes…" testid="customers-loader" />;
  }

  // Aggregate routing health across the whole base. Shown as a tappable
  // pill that filters the visible table to flagged customers only.
  const routingFlagged = customers.filter(c => {
    const s = diagnoseCustomerRouting(c).status;
    return s === "broken_link" || s === "missing_link";
  });

  return (
    <div data-testid="customers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clientes
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona tu base de clientes</p>
          {routingFlagged.length > 0 && (
            <p
              data-testid="customers-routing-warning"
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm"
            >
              <span className="font-bold">{routingFlagged.length}</span>
              cliente{routingFlagged.length === 1 ? "" : "s"} no podrá{routingFlagged.length === 1 ? "" : "n"} entrar en rutas — falta link de Maps válido o ubicación.
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button
              onClick={handleBulkDelete}
              variant="outline"
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="bulk-delete-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar ({selectedIds.length})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="download-template-button">
                <Download className="h-4 w-4 mr-2" />
                Plantilla
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => downloadTemplate("excel")} className="rounded-sm cursor-pointer" data-testid="download-template-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadTemplate("csv")} className="rounded-sm cursor-pointer" data-testid="download-template-csv">
                <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="import-customers-input"
            />
            <Button
              type="button"
              onClick={(e) => e.currentTarget.previousElementSibling.click()}
              variant="outline"
              className="rounded-sm"
              data-testid="import-customers-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar
            </Button>
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-customers-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar clientes
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-64" data-testid="export-customers-menu">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Elegí orden y formato
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="rounded-sm cursor-pointer" data-testid="export-customers-alpha-trigger">
                  <ArrowDownAZ className="h-4 w-4 mr-2 text-[rgb(0,47,167)]" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Orden alfabético</span>
                    <span className="text-[11px] text-slate-500">A → Z por nombre</span>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="rounded-sm w-52">
                    <DropdownMenuItem onClick={() => exportCustomers("pdf", "alpha")} className="rounded-sm cursor-pointer" data-testid="export-customers-alpha-pdf">
                      <FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportCustomers("excel", "alpha")} className="rounded-sm cursor-pointer" data-testid="export-customers-alpha-excel">
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" /> Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportCustomers("csv", "alpha")} className="rounded-sm cursor-pointer" data-testid="export-customers-alpha-csv">
                      <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="rounded-sm cursor-pointer" data-testid="export-customers-zone-trigger">
                  <MapPin className="h-4 w-4 mr-2 text-amber-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">Por zona</span>
                    <span className="text-[11px] text-slate-500">Provincia → cantón → distrito</span>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="rounded-sm w-52">
                    <DropdownMenuItem onClick={() => exportCustomers("pdf", "zone")} className="rounded-sm cursor-pointer" data-testid="export-customers-zone-pdf">
                      <FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportCustomers("excel", "zone")} className="rounded-sm cursor-pointer" data-testid="export-customers-zone-excel">
                      <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" /> Excel
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => exportCustomers("csv", "zone")} className="rounded-sm cursor-pointer" data-testid="export-customers-zone-csv">
                      <FileType2 className="h-4 w-4 mr-2 text-slate-600" /> CSV
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingCustomer(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-sm" data-testid="add-customer-button">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCustomer ? "Editar Cliente" : "Nuevo Cliente"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Código</label>
                    <Input
                      data-testid="customer-codigo-input"
                      value={formData.codigo || ""}
                      onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                      placeholder="Dejá en blanco para auto-generar según provincia/cantón/distrito"
                      className="rounded-sm mono"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Si no lo indicás, se asigna uno único derivado del código postal del distrito (ej: <span className="mono">CL-40701-A2F3</span>).
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Nombre de Facturación *</label>
                    <Input
                      data-testid="customer-name-input"
                      value={formData.nombre_facturacion}
                      onChange={(e) => setFormData({ ...formData, nombre_facturacion: e.target.value })}
                      required
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Nombre de Fantasía</label>
                    <Input
                      value={formData.nombre_fantasia}
                      onChange={(e) => setFormData({ ...formData, nombre_fantasia: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Teléfono 1</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Teléfono 2</label>
                    <Input
                      value={formData.phone2}
                      onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Email</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Provincia</label>
                    <Input
                      value={formData.provincia}
                      onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Cantón</label>
                    <Input
                      value={formData.canton}
                      onChange={(e) => setFormData({ ...formData, canton: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Distrito</label>
                    <Input
                      value={formData.distrito}
                      onChange={(e) => setFormData({ ...formData, distrito: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">GAM</label>
                    <Input
                      value={formData.gam}
                      onChange={(e) => setFormData({ ...formData, gam: e.target.value })}
                      className="rounded-sm"
                      placeholder="GAM / No GAM"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Dirección Exacta</label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Categoría</label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="rounded-sm"
                      placeholder="Minorista, Supermercado..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Código de Actividad Económica</label>
                    <Input
                      value={formData.actividad_economica}
                      onChange={(e) => setFormData({ ...formData, actividad_economica: e.target.value })}
                      className="rounded-sm"
                      placeholder="Ej: 4711 — Venta al por menor en almacenes no especializados"
                      data-testid="customer-actividad-input"
                    />
                    <p className="text-[11px] text-slate-500 mt-1">Lo necesitás para emitir facturas electrónicas. Buscalo en <a href="https://www.hacienda.go.cr/ATV/Login.aspx" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">ATV Hacienda</a> → Mis actividades.</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Vendedor Asignado</label>
                    {sellers.length > 0 ? (
                      <Select
                        value={formData.vendedor_asignado || "__none__"}
                        onValueChange={(v) => setFormData({ ...formData, vendedor_asignado: v === "__none__" ? "" : v })}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="customer-vendedor-select">
                          <SelectValue placeholder="Seleccionar vendedor..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin asignar —</SelectItem>
                          {sellers.map((s) => (
                            <SelectItem key={s.id} value={s.nombre_completo}>
                              <span className="inline-flex items-center gap-2">
                                <span
                                  className="h-2.5 w-2.5 rounded-full inline-block"
                                  style={{ background: s.color_vendedor || "#10B981" }}
                                />
                                <span>{s.nombre_completo}</span>
                                {s.codigo_vendedor && (
                                  <span className="text-[10px] font-mono text-slate-500">({s.codigo_vendedor})</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={formData.vendedor_asignado}
                        onChange={(e) => setFormData({ ...formData, vendedor_asignado: e.target.value })}
                        className="rounded-sm"
                        placeholder="Configurá vendedores en Mi Empresa → Mis Vendedores"
                      />
                    )}
                    <p className="text-[10px] text-slate-400 mt-1">
                      {sellers.length > 0
                        ? "Lista tomada de Mi Empresa → Mis Vendedores."
                        : "Marcá un colaborador como vendedor en Mi Empresa para verlos aquí."}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Link de Google Maps</label>
                    <Input
                      value={formData.maps_link}
                      onChange={(e) => setFormData({ ...formData, maps_link: e.target.value })}
                      className="rounded-sm"
                      placeholder="https://maps.app.goo.gl/..."
                      data-testid="customer-maps-link-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1.5">Notas</label>
                    <Input
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="rounded-sm"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="btn-primary rounded-sm" data-testid="save-customer-button">
                    {editingCustomer ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <CustomerImportPreviewDialog
            open={isPreviewDialogOpen}
            onOpenChange={setIsPreviewDialogOpen}
            rows={csvPreviewData}
            onCancel={() => { setIsPreviewDialogOpen(false); setCsvPreviewData([]); }}
            onConfirm={finalizarImportacion}
          />
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <ArrowUpDown className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-medium text-slate-700 uppercase tracking-wider">Ordenar por:</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-60" data-testid="customers-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-700 ml-auto">{sortedCustomers.length} cliente{sortedCustomers.length === 1 ? "" : "s"}</span>
        </div>
      </Card>

      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {sortedCustomers.length === 0 ? (
          <EmptyState
            icon={Users}
            title={customers.length === 0 ? "Aún no hay clientes" : "Sin coincidencias"}
            description={
              customers.length === 0
                ? "Crea tu primer cliente o importa tu cartera desde un archivo CSV para empezar."
                : "No encontramos clientes con esos filtros. Probá ajustando la búsqueda."
            }
            actionLabel={customers.length === 0 ? "Nuevo cliente" : undefined}
            actionOnClick={customers.length === 0 ? () => { resetForm(); setIsDialogOpen(true); } : undefined}
            testid="customers-empty-state"
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 w-12">
                  <Checkbox
                    checked={sortedCustomers.length > 0 && selectedIds.length === sortedCustomers.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Código</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Nombre Facturación</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Nombre Fantasía</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Teléfono</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Email</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Ubicación</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Categoría</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Saldo CxC</th>
                <th className="text-right py-3 px-4 font-medium text-slate-700">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedCustomers.map((customer) => (
                  <tr 
                    key={customer.id} 
                    className="table-row border-b border-slate-100 cursor-pointer" 
                    data-testid="customer-row"
                    onClick={() => handleEdit(customer)}
                  >
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(customer.id)}
                        onCheckedChange={() => toggleSelect(customer.id)}
                        data-testid={`select-customer-${customer.id}`}
                      />
                    </td>
                    <td className="py-3 px-4 mono text-xs text-slate-600">{customer.codigo || <span className="text-slate-300">—</span>}</td>
                    <td className="py-3 px-4 font-medium">
                      <div className="flex items-center gap-1.5">
                        <span>{customer.nombre_facturacion}</span>
                        <RoutingBadge customer={customer} />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{customer.nombre_fantasia || '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <span>{customer.phone || '-'}</span>
                        {customer.phone && (
                          <WhatsAppChatIconButton phone={customer.phone} testid={`customer-wa-${customer.id}`} />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{customer.email || '-'}</td>
                    <td className="py-3 px-4 text-slate-600">
                      {[customer.provincia, customer.canton, customer.distrito].filter(Boolean).join(', ') || '-'}
                    </td>
                    <td className="py-3 px-4">{customer.category || '-'}</td>
                    <td className="py-3 px-4 text-right" data-testid={`customer-balance-${customer.id}`}>
                      {(() => {
                        const b = getBalanceFor(customer);
                        if (b.saldo_pendiente > 0) {
                          return (
                            <span className="font-bold mono text-rose-700" title={`${b.facturas_pendientes} factura(s) pendiente(s)`}>
                              {formatMoney(b.saldo_pendiente)}
                            </span>
                          );
                        }
                        if (b.saldo_a_favor > 0) {
                          return (
                            <span className="font-medium mono text-emerald-700" title="Saldo a favor del cliente">
                              -{formatMoney(b.saldo_a_favor)}
                            </span>
                          );
                        }
                        return <span className="text-slate-300">—</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          data-testid={`edit-customer-${customer.id}`}
                          onClick={() => handleEdit(customer)}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-customer-${customer.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(customer.id);
                          }}
                          variant="ghost"
                          size="sm"
                          className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        )}
      </Card>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'bulk'
                ? `Estás a punto de eliminar ${selectedIds.length} cliente(s). Esta acción no se puede deshacer.`
                : 'Estás a punto de eliminar este cliente. Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}